import { DocumentNode, useMutation } from '@apollo/client';
import {
  NotesSection,
  NotesSectionProps,
} from 'common/components/NotesSection';
import { NoteAddMutationProps } from 'common/components/NotesSection/NotesForm';
import { NotesEntityFields } from 'common/graphql/__generated__/NotesEntityFields';
import { FieldValues, useFormContext } from 'react-hook-form';

export interface GenericNotesProps<
  TFormValues extends FieldValues,
  TMutationResult,
  TMutationVariables
> {
  entityId: string;
  data: NotesEntityFields[];
  loading?: boolean;
  mutationDocument: DocumentNode;
  isViewOnly?: boolean;
  listRefreshRequest?: NotesSectionProps['listRefreshRequest']; // ✅ Common prop here
}

export function GenericNotesSection<
  TFormValues extends FieldValues,
  TMutationResult,
  TMutationVariables
>({
  entityId,
  data,
  loading,
  mutationDocument,
  listRefreshRequest,
  isViewOnly,
}: GenericNotesProps<TFormValues, TMutationResult, TMutationVariables>) {
  let isDirty = false;
  try {
    const form = useFormContext<TFormValues>();
    isDirty = form.formState.isDirty;
  } catch {
    // Not in a FormProvider — ignore
  }

  const [createNote, { loading: createNoteInProgress, error }] = useMutation<
    TMutationResult,
    TMutationVariables
  >(mutationDocument);

  const noteAddMutation: NoteAddMutationProps = {
    loading: createNoteInProgress,
    createNote: async (formData) => {
      const { noteComment, isShared } = { ...formData };

      await createNote({
        variables: {
          input: {
            note: {
              entityId,
              noteComment: noteComment!,
              isShared: isShared!,
            },
          },
        } as TMutationVariables,
      });

      if (!error) listRefreshRequest?.();
      return error;
    },
  };

  return (
    <NotesSection
      dataLoading={loading ?? false}
      noteAddMutation={noteAddMutation}
      addingNoteInProgress={createNoteInProgress}
      listRefreshRequest={listRefreshRequest}
      data={data}
      isDirty={isDirty}
      isViewOnly={isViewOnly}
    />
  );
}
